<template>
  <div class="home">
    <div class="menu" v-if="env !== 'production'">
      <Button @click="menuOpened = !menuOpened" class="text-shadow">Menu</Button>
      <div class="menu__list" v-if="menuOpened">
        <Button @click="$store.commit('setStep', 'start')">0</Button>
        <Button @click="$store.commit('setStep', 'temperature-opening')">1</Button>
        <Button @click="$store.commit('setStep', 'temperature-interactive')">2</Button>
        <Button @click="$store.commit('setStep', 'humidity-opening')">3</Button>
        <Button @click="$store.commit('setStep', 'humidity-interactive')">4</Button>
        <Button @click="$store.commit('setStep', 'clean-air-opening')">5</Button>
        <Button @click="$store.commit('setStep', 'clean-air-interactive')">6</Button>
        <Button @click="$store.commit('setStep', 'climate-control')">7</Button>
        <Button @click="$store.commit('setStep', 'finish')">8</Button>
        <Button @click="$store.commit('setStep', 'products')">9</Button>
      </div>
      <div class="menu__list" v-if="menuOpened">
        <Button @click="$store.commit('setModel', 0)">Model 1</Button>
        <Button @click="$store.commit('setModel', 1)">Model 2</Button>
        <Button @click="$store.commit('setModel', 2)">Model 3</Button>
        <Button @click="$store.commit('setModel', 3)">Model 4</Button>
        <Button @click="$store.commit('setModel', 4)">Model 5</Button>
        <Button @click="$store.commit('setModel', 5)">Model 6</Button>
        <Button @click="$store.commit('setModel', 6)">Model 7</Button>
        <Button @click="$store.commit('setModel', 7)">Model 8</Button>
        <Button @click="$store.commit('setModel', 8)">Model 9</Button>
        <Button @click="showClouds(0.7)">Show Clouds</Button>
        <Button @click="hideClouds()">Hide Clouds</Button>
      </div>
      <div style="display: flex">
        <Button @click="$i18n.locale = 'en'">en</Button>
        <Button @click="$i18n.locale = 'ua'">ua</Button>
      </div>
    </div>
    <transition name="fade">
      <div v-if="showPreloader" :style="{backgroundImage: 'url(' + require('@/assets/img/preloader-bg-image.png') + ')'}" class="preloader">
        <div class="main-frame">
          <img class="preloader__logo" :src="$t('logo')" alt="Logo">
          <div class="preloader__text-container">
            <p class="preloader__text">
              <span v-html="$t('preloader.title')"></span>
              <span class="preloader__dots-placeholder">...</span>
            </p>
          </div>
        </div>
      </div>
    </transition>

    <canvas class="home__bg" ref="canvasBg"></canvas>


    <div class="main-frame">
      <LanguageToggler />

      <!-- audio-->
      <AudioControl class="audio-control" v-if="bgMusicIsPLaying && !isPanel" :audio-is-muted="bgMusicIsMuted" @toggle="muteUnmuteBgMusic"/>

      <div class="home__model" :class="'home__model--step-' + step" @mousedown="planetInteraction">
        <canvas ref="canvas"></canvas>
      </div>

      <div class="home__content">
        <div class="home__logo" :class="'home__logo--step-' + step" @click="$store.commit('setStep', 'start'); stopBgMusic(); ">
          <img class="image-rendering-contrast-disabled" :src="$t('logo')" alt="logo">
        </div>

        <div class="home__steps">
          <transition name="fade">
            <StepStart @startBgMusic="startBgMusic" @next="next('temperature-opening')" v-if="step === 'start'"></StepStart>
          </transition>

          <transition name="fade">
            <StepTemperature @next="next('temperature-interactive')" v-if="step === 'temperature-opening'"></StepTemperature>
          </transition>

          <transition name="fade">
            <StepTemperatureIncrease @next="next('humidity-opening')" v-if="step === 'temperature-interactive'"></StepTemperatureIncrease>
          </transition>

          <transition name="fade">
            <StepHumidity @next="next('humidity-interactive')" v-if="step === 'humidity-opening'"></StepHumidity>
          </transition>

          <transition name="fade">
            <StepHumidityIncrease @next="next('clean-air-opening')" v-if="step === 'humidity-interactive'"></StepHumidityIncrease>
          </transition>

          <transition name="fade">
            <StepCleaningAir @next="next('clean-air-interactive')" v-if="step === 'clean-air-opening'"></StepCleaningAir>
          </transition>

          <transition name="fade">
            <StepIncreasePower @next="next('climate-control')" v-if="step === 'clean-air-interactive'"></StepIncreasePower>
          </transition>

          <transition name="fade">
            <StepClimateControl @next="next('finish')" v-if="step === 'climate-control'"></StepClimateControl>
          </transition>

          <transition name="fade">
            <StepEndTerraform @next="next('products')" v-if="step === 'finish'"></StepEndTerraform>
          </transition>

          <transition name="fade">
            <StepProducts @next="next('start')" v-if="step === 'products'"></StepProducts>
          </transition>
        </div>
      </div>

      <div class="socials" v-if="step === 'finish' && !isPanel">
        <div class="socials__text" v-html="$t('endTerraform.share')"></div>

        <div class="socials__list">
          <ShareNetwork
              @click="sharedFacebook"
              class="socials__social"
              network="facebook"
              :url="locationOrigin"
              :title="$t('share.title')"
          >
            <img src="@/assets/img/facebook.svg" alt="facebook">
          </ShareNetwork>

          <ShareNetwork
              @click="sharedTwitter"
              class="socials__social"
              network="twitter"
              :url="locationOrigin"
              :title="$t('share.title')"
          >
            <img src="@/assets/img/twitter.svg" alt="twitter">
          </ShareNetwork>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import throttle from 'lodash.throttle'
import { Howl, Howler } from 'howler'

import { mapState } from 'vuex'
import ObjThreeModelSingleton from '@/3d/core/ObjThreeModelSingleton'
import Button from '@/components/global/Button'
import BackgroundSphere from '@/3d/core/BackgroundSphere'
import smoothChangeMaterial from '@/3d/helpers/smoothChangeMaterial'

import StepStart from '@/components/steps/StepStart'
import StepTemperature from '@/components/steps/StepTemperature'
import StepTemperatureIncrease from '@/components/steps/StepTemperatureIncrease'
import StepHumidity from '@/components/steps/StepHumidity'
import StepHumidityIncrease from '@/components/steps/StepHumidityIncrease'
import StepCleaningAir from '@/components/steps/StepCleaningAir'
import StepIncreasePower from '@/components/steps/StepIncreasePower'
import StepEndTerraform from '@/components/steps/StepEndTerraform'
import StepClimateControl from '@/components/steps/StepClimateControl'
import StepProducts from '../components/steps/StepProducts'
import AudioControl from '../components/global/AudioControl'
import isTouchScreen from '@/helpers/isTouchScreen'
import LanguageToggler from "@/components/global/LanguageToggler";
import isIOS from '../helpers/isIOS'

const sound = new Howl({
  src: [require('@/assets/audio/background-music.mp3')],
  loop: true,
})

export default {
  name: 'Home',
  props: {},

  components: {
    LanguageToggler,
    Button,
    StepStart,
    StepTemperature,
    StepTemperatureIncrease,
    StepHumidity,
    StepHumidityIncrease,
    StepCleaningAir,
    StepIncreasePower,
    StepEndTerraform,
    StepClimateControl,
    StepProducts,
    AudioControl,
  },

  data () {
    return {
      menuOpened: false,
      modelPercentComplete: 0,
      modelStartLoading: false,
      model: {},
      clouds: 'Clouds',
      clouds2: 'Clouds2',
      clouds3: 'Clouds3',
      planet: 'Sphere',
      timerStart: Date.now(),
      showPreloader: true,
      bgMusicIsPLaying: false,
      bgMusicIsMuted: false,
      isPanel: false,
    }
  },

  mounted () {
    if(this.$router.currentRoute.fullPath === '/?lang=en') {
      this.$i18n.locale = 'en'
      localStorage.setItem('locale', 'en');
    }

    if(localStorage.getItem('locale') === 'en') {
      this.$router.push('/?lang=en')
    }

    if (!this.modelStartLoading) {
      this.modelStartLoading = true

      setTimeout(() => {
        let model = new ObjThreeModelSingleton({
          canvas: this.$refs.canvas,
          modelPath: 'models/obj2/MarsV02.obj',
          materialPath: 'models/obj2/Daikin_Mars_V02.mtl',
          enableZoom: false,
          zoomFactor: 4.1 / 1.2,
          position: {
            x: 2000,
            y: 1000,
            z: -1000,
          },
        })

        let sphereBg = new BackgroundSphere({
          canvas: this.$refs.canvasBg,
          position: {
            x: 0,
            y: 0,
            z: 2,
          },
        })

        model.initModel()
        sphereBg.initModel()

        let modelInterval = setInterval(() => {
          if (model.percentComplete > 100) {
            this.modelPercentComplete = model.percentComplete
            clearInterval(modelInterval)
          }
          else {
            this.modelPercentComplete = model.percentComplete
          }
        }, 100)
      }, 500)
    }

    setTimeout(() => {
      this.showPreloader = false
    }, 3000)
  },

  created () {

    this.$store.commit('setAnimationPlaying', false)

    this.$store.commit('setStep', 'start')

    function setUsefullDimensionVariables () {
      document.documentElement.style.setProperty('--useful-height', window.innerHeight + 'px')
      document.documentElement.style.setProperty('--useful-width', window.innerWidth + 'px')
    }

    setUsefullDimensionVariables()

    window.addEventListener('resize', throttle(() => {
      setUsefullDimensionVariables()
    }, 250))

    //audio
    this.isPanel = this.isBrowser('Chrome') && isTouchScreen() && window.innerWidth > 1000 && window.innerHeight > 1000

  },

  updated () {},

  methods: {
    next (nextStep) {
      this.$store.commit('setStep', nextStep)
    },

    showClouds (val) {
      let clouds = this.findObject(this.clouds)

      let interval = setInterval(() => {
        if (clouds.material.opacity < val) {
          clouds.material.opacity += 0.01
        }
        else {
          clearInterval(interval)
        }
      }, 10)
    },

    sharedFacebook () {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          'event': 'Event',
          'analytics_action': 'home-button-share-facebook',
          'analytics_label': this.step,
        })
      }
    },

    sharedTwitter () {
      if (this.$gtm) {
        this.$gtm.trackEvent({
          'event': 'Event',
          'analytics_action': 'home-button-share-twitter',
          'analytics_label': this.step,
        })
      }
    },

    planetInteraction () {
      // console.log('interaction')

      if (this.$gtm) {
        this.$gtm.trackEvent({
          'event': 'Event',
          'analytics_action': this.step + '-planet-interaction',
          'analytics_label': this.step,
        })
      }
    },

    hideClouds () {
      let clouds = this.findObject(this.clouds)

      // console.log('hide clouds')

      let interval = setInterval(() => {
        if (clouds.material.opacity > 0) {
          clouds.material.opacity -= 0.01
        }
        else {
          clearInterval(interval)
        }
      }, 1)
    },

    findObject (name) {
      let model = ObjThreeModelSingleton.getInstance()

      let clouds = {}
      model.root.traverse((o) => {
        // console.log('o', o)

        if (o.isMesh || o.isGroup) {
          if (o.name === name) {
            clouds = o
          }
        }
      })

      return clouds
    },

    startBgMusic () {
      sound.play()
      this.bgMusicIsPLaying = true

    },

    stopBgMusic () {
      sound.stop()
      // this.bgMusicIsPLaying = false
    },
    muteUnmuteBgMusic () {
      if (!this.bgMusicIsMuted) {
        Howler.volume(0)
        this.bgMusicIsMuted = true
      }
      else {
        Howler.volume(1)
        this.bgMusicIsMuted = false
      }
    },
  },

  watch: {
    step (val) {
      // console.log("val", val)
      // alert(val)
      if (val === 'humidity-interactive') {
        this.showClouds(1)
      }
    },

    modelNum (val, oldVal) {
      // console.log('store.state.animationPlaying', this.$store.state.animationPlaying)
      let model = ObjThreeModelSingleton.getInstance()

      let planet = this.findObject(this.planet)

      smoothChangeMaterial(model.materialsArr[oldVal], model.materialsArr[val], 'planet', planet)
    },

    cloudsNum (val) {
      let model = ObjThreeModelSingleton.getInstance()

      let clouds = this.findObject(this.clouds)

      clouds.material = model.materialsCloudsArr[val]
    },

    modelPercentComplete (val) {
      let model = ObjThreeModelSingleton.getInstance()

      if (val === 101) {

        model.renderer.setPixelRatio(window.devicePixelRatio * 2)

        model.root.traverse((o) => {
          if (o.isMesh) {
            // console.log('o', o)
            // console.log('o.name', o.name)
            // console.log('o.material', o.material)
          }
        })

        this.hideClouds()
      }
    },
  },

  computed: {
    env: () => {
      return process.env.NODE_ENV
    },

    locationOrigin: () => {
      return window.location.origin
    },

    ...mapState({
      step: state => state.step,
      modelNum: state => state.model,
      cloudsNum: state => state.clouds,
    }),
  },
}
</script>

<style lang="scss" scoped>

</style>
